import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './AnalysisGrid.css';
import api from '../api/axiosConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format, isValid, startOfMonth, subMonths, endOfMonth } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// 숫자 포맷
const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '';
    const number = parseFloat(num.toString().replace(/,/g, ''));
    return isNaN(number) ? '' : number.toLocaleString();
};

const TestPage4 = () => {
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);

    const today = new Date();
    const threeMonthsAgo = subMonths(today, 3);
    const [startDate, setStartDate] = useState(startOfMonth(threeMonthsAgo));
    const [endDate, setEndDate] = useState(endOfMonth(today));

    const [fieldId, setFieldId] = useState('');
    const [fieldOptions, setFieldOptions] = useState([]);

    const [rowsWithSubtotal, setRowsWithSubtotal] = useState([]);

    // ⭐️ 합계 상태 분리
    const [totalRow, setTotalRow] = useState({});       // 전체 합계
    const [taxInvoiceRow, setTaxInvoiceRow] = useState({}); // 세금계산서 합계 (ETC_YN !== 'Y')
    const [etcRow, setEtcRow] = useState({});           // 기타경비 합계 (ETC_YN === 'Y')

    // 현장 조회
    useEffect(() => {
        api.get(`/api/fieldList`)
            .then((res) => {
                const uniqueOptions = res.data.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.FIELD_ID === item.FIELD_ID)
                );
                setFieldOptions(uniqueOptions);
            })
            .catch((err) => console.error(err));
    }, []);

    // 세금계산서 현황 조회
    const searchList = useCallback(() => {
        const start = format(startDate, 'yyyy-MM-dd');
        const end = format(endDate, 'yyyy-MM-dd');

        api.get(`/api/analysis/searchList2?startDate=${start}&endDate=${end}&fieldId=${fieldId || ''}`)
            .then((res) => {
                const rawData = res.data || [];
                const processedData = rawData.map(row => {
                    let month = row.ISSUED_MONTH;
                    let day = row.ISSUED_DAY;
                    if (!month && row.ISSUED_DATE && row.ISSUED_DATE.length >= 10) {
                        month = row.ISSUED_DATE.substring(5, 7);
                        day = row.ISSUED_DATE.substring(8, 10);
                    }
                    return {
                        ...row,
                        ISSUED_MONTH: month,
                        ISSUED_DAY: day,
                        ETC_YN: row.ETC_YN || 'N'
                    };
                });
                setData(processedData);
                setRows(processedData);
            })
            .catch((err) => console.error(err));
    }, [startDate, endDate, fieldId]);

    useEffect(() => {
        searchList();
    }, [searchList]);


    // ----------------------------------------------------
    // ⭐️ 소계/월계/총계 및 분리 합계 계산 로직
    // ----------------------------------------------------
    const calculateSubtotals = (list) => {
        if (!list || list.length === 0) {
            setRowsWithSubtotal([]);
            setTotalRow({});
            setTaxInvoiceRow({});
            setEtcRow({});
            return;
        }

        const result = [];
        const createZeroObj = () => ({
            OUT_PROVIDE_AMOUNT: 0, OUT_VAT: 0, OUT_TOTAL_AMOUNT: 0, UNPAID_AMOUNT_OUT: 0,
            IN_PROVIDE_AMOUNT: 0, IN_VAT: 0, IN_TOTAL_AMOUNT: 0, IN_PAID_AMOUNT: 0, REMAIN_AMOUNT: 0,
        });

        const accumulate = (target, source) => {
            for (const key in target) {
                target[key] += (parseFloat(source[key]) || 0);
            }
        };

        // 누적 변수들
        let currentGroupTotal = createZeroObj(); // ETC 소계
        let monthlyTotal = createZeroObj();      // 월계

        let grandTotal = createZeroObj();        // 전체 총계
        let taxSum = createZeroObj();            // 세금계산서 합계 (ETC_YN !== 'Y')
        let etcSum = createZeroObj();            // 기타경비 합계 (ETC_YN === 'Y')

        let currentMonth = list[0].ISSUED_MONTH;
        let currentETC = list[0].ETC_YN;

        const pushTotalRow = (type, monthStr) => {
            let targetData = type === 'ETC' ? currentGroupTotal : monthlyTotal;
            let label = type === 'ETC' ? '소계' : '월계';

            if (targetData.OUT_TOTAL_AMOUNT !== 0 || targetData.IN_TOTAL_AMOUNT !== 0) {
                result.push({
                    CUSTOMER_NAME: label,
                    ISSUED_MONTH: monthStr,
                    ISSUED_DAY: '',
                    ...targetData,
                    isSubtotal: true,
                    isEtctotal: type === 'ETC',
                    isMonthlyTotal: type === 'MONTH',
                });
            }

            if (type === 'ETC') {
                accumulate(monthlyTotal, currentGroupTotal);
                currentGroupTotal = createZeroObj();
            } else if (type === 'MONTH') {
                // 월계는 총계에 누적 (이미 개별 행 처리에서 taxSum, etcSum은 계산됨)
                accumulate(grandTotal, monthlyTotal);
                monthlyTotal = createZeroObj();
            }
        };

        list.forEach((row) => {
            const rowMonth = row.ISSUED_MONTH;
            const rowETC = row.ETC_YN;

            // 1. 그룹 변경 체크
            if (rowMonth !== currentMonth) {
                pushTotalRow('ETC', currentMonth);
                pushTotalRow('MONTH', currentMonth);
                currentMonth = rowMonth;
                currentETC = rowETC;
            } else if (rowETC !== currentETC) {
                pushTotalRow('ETC', currentMonth);
                currentETC = rowETC;
            }

            // 2. 현재 행 추가 및 소계 누적
            result.push(row);
            accumulate(currentGroupTotal, row);

            // 3. ⭐️ 총괄 합계 분리 계산 ⭐️
            if (row.ETC_YN === 'Y') {
                accumulate(etcSum, row); // 기타경비 합계 누적
            } else {
                accumulate(taxSum, row); // 세금계산서 합계 누적
            }
        });

        // 마지막 그룹 처리
        pushTotalRow('ETC', currentMonth);
        pushTotalRow('MONTH', currentMonth);

        // 결과 저장
        setRowsWithSubtotal(result);
        setTotalRow(grandTotal);
        setTaxInvoiceRow(taxSum);
        setEtcRow(etcSum);
    };

    useEffect(() => {
        calculateSubtotals(rows);
    }, [rows]);


    // =========================
    // 엑셀 / 인쇄
    // =========================
    const handlePrint = () => {
        const printContents = document.getElementById('print-area').innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload();
    };

// 기존 exportToExcel 함수를 아래 코드로 교체하세요.

const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('매출매입내역');

    // ── 스타일 정의 ──
    const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    const headerFont = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    const subtotalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
    const etctotalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };
    const totalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
    const grandHeaderFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF305496' } };
    const yellowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF60F' } };
    const thinBorder = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' },
    };
    const numFmt = '#,##0';

    const applyStyle = (cell, { fill, font, alignment, border, numberFormat } = {}) => {
        if (fill) cell.fill = fill;
        if (font) cell.font = font;
        if (alignment) cell.alignment = alignment;
        if (border) cell.border = border;
        if (numberFormat) cell.numFmt = numberFormat;
    };

    const setRowStyles = (row, fill, font) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = thinBorder;
            if (fill) cell.fill = fill;
            if (font) cell.font = font;
        });
    };

    const numVal = (v) => parseFloat(v) || 0;

    // ── 제목 행 ──
    const fieldName = fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || '';
    let titleText = '';
    if (rows.length > 0) {
        titleText = `현장: ${fieldName || '전체'} / 기간: ${rows[0].ISSUED_DATE.substring(0, 7)} ~ ${rows[rows.length - 1].ISSUED_DATE.substring(0, 7)}`;
    }
    ws.mergeCells('A1:N1');
    const titleCell = ws.getCell('A1');
    titleCell.value = titleText || '현장별 매출, 매입 내역';
    titleCell.font = { bold: true, size: 13 };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
    ws.getRow(1).height = 28;

    // ── 헤더 (2행) ──
    // 행2: 월, 일, 거래처, 매출(4), 매입(5), 차액, 정산일, 비고
    const headerRow1 = ['월', '일', '거래처', '매출', '', '', '', '매입', '', '', '', '', '매출-매입(차액)', '정산일', '비고'];
    const headerRow2 = ['', '', '', '공급가액', '세액', '합계', '미결금', '공급가액', '세액', '합계', '결제금액', '잔금', '', '', ''];

    const r2 = ws.addRow(headerRow1); // row 2
    const r3 = ws.addRow(headerRow2); // row 3

    // 병합: 월(A2:A3), 일(B2:B3), 거래처(C2:C3)
    ws.mergeCells('A2:A3');
    ws.mergeCells('B2:B3');
    ws.mergeCells('C2:C3');
    // 매출(D2:G2), 매입(H2:L2)
    ws.mergeCells('D2:G2');
    ws.mergeCells('H2:L2');
    // 차액(M2:M3), 정산일(N2:N3), 비고(O2:O3)
    ws.mergeCells('M2:M3');
    ws.mergeCells('N2:N3');
    ws.mergeCells('O2:O3');

    [r2, r3].forEach(r => {
        r.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = headerFill;
            cell.font = headerFont;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = thinBorder;
        });
        r.height = 22;
    });

    // ── 컬럼 너비 ──
    ws.columns = [
        { width: 6 },   // A: 월
        { width: 6 },   // B: 일
        { width: 22 },  // C: 거래처
        { width: 14 },  // D: 매출 공급가액
        { width: 12 },  // E: 매출 세액
        { width: 14 },  // F: 매출 합계
        { width: 14 },  // G: 미결금
        { width: 14 },  // H: 매입 공급가액
        { width: 12 },  // I: 매입 세액
        { width: 14 },  // J: 매입 합계
        { width: 14 },  // K: 결제금액
        { width: 14 },  // L: 잔금
        { width: 16 },  // M: 차액
        { width: 12 },  // N: 정산일
        { width: 20 },  // O: 비고
    ];

    // ── 데이터 행 (월 병합 포함) ──
    let i = 0;
    while (i < rowsWithSubtotal.length) {
        const currentMonth = rowsWithSubtotal[i].ISSUED_MONTH;

        // 같은 월 span 계산
        let spanCount = 0;
        for (let j = i; j < rowsWithSubtotal.length; j++) {
            if (rowsWithSubtotal[j].ISSUED_MONTH === currentMonth) spanCount++;
            else break;
        }

        const startRowNum = ws.rowCount + 1;

        for (let k = 0; k < spanCount; k++) {
            const row = rowsWithSubtotal[i + k];
            const isSubtotal = row.isSubtotal;
            const diff = numVal(row.OUT_TOTAL_AMOUNT) - numVal(row.IN_TOTAL_AMOUNT);

            const rowData = [
                k === 0 ? currentMonth : '',           // A: 월
                isSubtotal ? row.CUSTOMER_NAME : row.ISSUED_DAY, // B: 일 또는 소계/월계
                isSubtotal ? '' : row.CUSTOMER_NAME,    // C: 거래처
                numVal(row.OUT_PROVIDE_AMOUNT),
                numVal(row.OUT_VAT),
                numVal(row.OUT_TOTAL_AMOUNT),
                numVal(row.UNPAID_AMOUNT_OUT),
                numVal(row.IN_PROVIDE_AMOUNT),
                numVal(row.IN_VAT),
                numVal(row.IN_TOTAL_AMOUNT),
                numVal(row.IN_PAID_AMOUNT),
                numVal(row.REMAIN_AMOUNT),
                isSubtotal ? diff : '',                 // M: 차액
                row.PAID_DATE || '',
                row.REMARK || '',
            ];

            const excelRow = ws.addRow(rowData);
            excelRow.eachCell({ includeEmpty: true }, (cell) => {
                cell.border = thinBorder;
                cell.alignment = { vertical: 'middle' };
            });

            // 숫자 포맷 (D~M)
            for (let col = 4; col <= 13; col++) {
                const cell = excelRow.getCell(col);
                if (typeof cell.value === 'number' && cell.value !== 0) {
                    cell.numFmt = numFmt;
                } else if (cell.value === 0) {
                    cell.value = '';
                }
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
            }

            // 월/일 가운데 정렬
            excelRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            excelRow.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };

            // 소계/월계 스타일
            if (isSubtotal) {
                // 일+거래처 병합
                ws.mergeCells(excelRow.number, 2, excelRow.number, 3);
                excelRow.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
                excelRow.getCell(2).font = { bold: true };

                const fillColor = row.isEtctotal ? etctotalFill : subtotalFill;
                setRowStyles(excelRow, fillColor, { bold: true, size: 10 });
            }

            // 미결금/잔금 하이라이트
            if (!isSubtotal) {
                if (numVal(row.UNPAID_AMOUNT_OUT) > 0) {
                    excelRow.getCell(7).fill = yellowFill;
                }
                if (numVal(row.REMAIN_AMOUNT) > 0) {
                    excelRow.getCell(12).fill = yellowFill;
                }
            }
        }

        // 월 셀 병합
        if (spanCount > 1) {
            ws.mergeCells(startRowNum, 1, startRowNum + spanCount - 1, 1);
        }
        ws.getCell(`A${startRowNum}`).alignment = { horizontal: 'center', vertical: 'middle' };
        ws.getCell(`A${startRowNum}`).font = { bold: true };

        i += spanCount;
    }

    // ── 합계 행 ──
    const totalData = [
        '', '합계', '',
        numVal(totalRow.OUT_PROVIDE_AMOUNT),
        numVal(totalRow.OUT_VAT),
        numVal(totalRow.OUT_TOTAL_AMOUNT),
        numVal(totalRow.UNPAID_AMOUNT_OUT),
        numVal(totalRow.IN_PROVIDE_AMOUNT),
        numVal(totalRow.IN_VAT),
        numVal(totalRow.IN_TOTAL_AMOUNT),
        numVal(totalRow.IN_PAID_AMOUNT),
        numVal(totalRow.REMAIN_AMOUNT),
        numVal(totalRow.OUT_TOTAL_AMOUNT) - numVal(totalRow.IN_TOTAL_AMOUNT),
        '', '',
    ];
    const totalExcelRow = ws.addRow(totalData);
    ws.mergeCells(totalExcelRow.number, 1, totalExcelRow.number, 3);
    totalExcelRow.getCell(1).value = '합계';
    totalExcelRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    setRowStyles(totalExcelRow, totalFill, { bold: true, size: 10 });
    for (let col = 4; col <= 13; col++) {
        const cell = totalExcelRow.getCell(col);
        if (typeof cell.value === 'number' && cell.value !== 0) cell.numFmt = numFmt;
        else if (cell.value === 0) cell.value = '';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }
    totalExcelRow.getCell(13).font = { bold: true, color: { argb: 'FF0000FF' }, size: 10 };

    // ── 빈 행 ──
    ws.addRow([]);

    // ── 총괄 섹션 ──
    // 1) 5개 행 모두 먼저 추가
    const gh1 = ws.addRow(['총괄', '구분', '', '매출', '', '', '', '매입', '', '', '', '', '매출-매입(차액)', '', '']);
    const gh2 = ws.addRow(['', '', '', '공급가액', '세액', '합계', '미결금', '공급가액', '세액', '합계', '결제금액', '잔금', '공급가', '부가세', '합계']);

    const buildGrandData = (label, src) => {
        const op = numVal(src.OUT_PROVIDE_AMOUNT);
        const ov = numVal(src.OUT_VAT);
        const ot = numVal(src.OUT_TOTAL_AMOUNT);
        const ip = numVal(src.IN_PROVIDE_AMOUNT);
        const iv = numVal(src.IN_VAT);
        const it = numVal(src.IN_TOTAL_AMOUNT);
        return ['', label, '', op, ov, ot, numVal(src.UNPAID_AMOUNT_OUT), ip, iv, it, numVal(src.IN_PAID_AMOUNT), numVal(src.REMAIN_AMOUNT), op - ip, ov - iv, ot - it];
    };

    const gd1 = ws.addRow(buildGrandData('세금계산서 합계', taxInvoiceRow));
    const gd2 = ws.addRow(buildGrandData('기타경비', etcRow));
    const gd3 = ws.addRow(buildGrandData('합계', totalRow));

    // 2) 행 번호 확인 후 병합
    const r1 = gh1.number; // 총괄 헤더1
    const r2g = gh2.number; // 총괄 헤더2

    // 총괄 (A열, 5행 병합)
    ws.mergeCells(r1, 1, r1 + 4, 1);
    // 구분 (B~C열, 2행 병합)
    ws.mergeCells(r1, 2, r2g, 3);
    // 매출 (D~G열)
    ws.mergeCells(r1, 4, r1, 7);
    // 매입 (H~L열)
    ws.mergeCells(r1, 8, r1, 12);
    // 매출-매입(차액) (M~O열)
    ws.mergeCells(r1, 13, r1, 15);
    // 데이터 행 B~C 병합
    ws.mergeCells(gd1.number, 2, gd1.number, 3);
    ws.mergeCells(gd2.number, 2, gd2.number, 3);
    ws.mergeCells(gd3.number, 2, gd3.number, 3);

    // 3) 헤더 스타일
    [gh1, gh2].forEach(r => {
        r.height = 22;
        r.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = grandHeaderFill;
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = thinBorder;
        });
    });

    // 4) 데이터 행 스타일
    [gd1, gd2, gd3].forEach((r, idx) => {
        r.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = thinBorder;
            cell.alignment = { vertical: 'middle' };
        });
        // 구분 라벨 스타일
        r.getCell(2).font = { bold: true, size: 10 };
        r.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
        // 숫자 포맷
        for (let col = 4; col <= 15; col++) {
            const cell = r.getCell(col);
            if (typeof cell.value === 'number' && cell.value !== 0) cell.numFmt = numFmt;
            else if (cell.value === 0) cell.value = '';
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
        // 합계 행 배경
        if (idx === 2) {
            setRowStyles(r, totalFill, { bold: true, size: 10 });
            r.getCell(15).fill = yellowFill;
        }
    });

    // 총괄 셀 스타일 보정 (병합 후 덮어쓰기)
    ws.getCell(`A${r1}`).fill = grandHeaderFill;
    ws.getCell(`A${r1}`).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
    ws.getCell(`A${r1}`).alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(`A${r1}`).border = thinBorder;

    // ── 다운로드 ──
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `매출매입내역_${fieldName || '전체'}_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}.xlsx`;
    saveAs(blob, fileName);
};

    // =========================
    // 렌더링
    // =========================
    return (
        <div className="grid-container">
            <h2>현장별 매출, 매입 내역</h2>

            <div className="gh-band">
                <div className="gh-band__row">
                    <div className="gh-band__title">
                        <span className="gh-octo" aria-hidden>▶</span>
                        <span>조회조건</span>
                    </div>
                </div>
                <div className="gh-band__filters">

                    <div className="filter-item gh-field">
                        <label>현장</label>
                        <Autocomplete
                            options={fieldOptions}
                            value={fieldOptions.find((option) => option.FIELD_ID === fieldId) || null}
                            onChange={(event, newValue) => {
                                setFieldId(newValue ? newValue.FIELD_ID : '');
                            }}
                            getOptionLabel={(option) => option?.FIELD_NAME || ''}
                            isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="현장 선택"
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    sx={{ px: 1, minWidth: 200 }}
                                />
                            )}
                        />
                    </div>
                    <div className="filter-actions gh-actions">
                        <button className="btn" onClick={searchList}>조회</button>
                    </div>
                </div>
            </div>

            <div className="button-group-right">
                <button onClick={exportToExcel} className="btn btn-primary">엑셀 내려받기</button>
                <button onClick={handlePrint} className="btn btn-ghost">출력하기</button>
            </div>

            <div id="print-area">
                <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    {rows.length > 0
                        ? `현장: ${fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || '현장을 선택해주세요 '} / 기간: ${rows[0].ISSUED_DATE.substring(0, 7)} ~ ${rows[rows.length - 1].ISSUED_DATE.substring(0, 7)}`
                        : ''
                    }

                </div>

                <div className="grid-table-wrapper">
                    <table className="grid-table">
                        <thead>
                            <tr>
                                <th rowSpan="2" style={{ width: '50px' }}>월</th>
                                <th rowSpan="2" style={{ width: '50px' }}>일</th>
                                <th rowSpan="2">거래처</th>
                                <th colSpan="4">매출</th>
                                <th colSpan="5" className="header-purchase">매입</th>
                                <th></th><th></th><th></th>
                            </tr>
                            <tr>
                                <th>공급가액</th><th>세액</th><th>합계</th><th>미결금</th>
                                <th>공급가액</th><th>세액</th><th>합계</th><th>결제금액</th><th>잔금</th>
                                <th>매출-매입(차액)</th><th>정산일</th><th>비고</th>
                            </tr>
                        </thead>

                        <tbody>
                            {(() => {
                                const result = [];
                                let i = 0;

                                while (i < rowsWithSubtotal.length) {
                                    const currentRow = rowsWithSubtotal[i];
                                    const currentMonth = currentRow.ISSUED_MONTH;

                                    // RowSpan 계산
                                    let spanCount = 0;
                                    for (let j = i; j < rowsWithSubtotal.length; j++) {
                                        if (rowsWithSubtotal[j].ISSUED_MONTH === currentMonth) {
                                            spanCount++;
                                        } else {
                                            break;
                                        }
                                    }

                                    for (let k = 0; k < spanCount; k++) {
                                        const row = rowsWithSubtotal[i + k];
                                        const isSubtotal = row.isSubtotal;
                                        const diff = (row.OUT_TOTAL_AMOUNT || 0) - (row.IN_TOTAL_AMOUNT || 0);

                                        result.push(
                                            <tr key={i + k} className={row.isEtctotal ? 'etctotal-row' : (isSubtotal ? 'subtotal-row' : '')}>

                                                {/* 월 컬럼 */}
                                                {k === 0 && (
                                                    <td rowSpan={spanCount} style={{ width: '50px', background: '#fff', verticalAlign: 'middle', fontWeight: 'bold' }}>
                                                        {currentMonth}
                                                    </td>
                                                )}

                                                {/* 일/거래처 컬럼 */}
                                                <td colSpan={isSubtotal ? 2 : 1} style={{ textAlign: isSubtotal ? 'center' : 'center' }}>
                                                    {isSubtotal ? <strong>{row.CUSTOMER_NAME}</strong> : row.ISSUED_DAY}
                                                </td>

                                                {!isSubtotal && (
                                                    <td style={{ textAlign: 'left' }}>{row.CUSTOMER_NAME}</td>
                                                )}

                                                {/* 금액 데이터 */}
                                                <td><span className="right-align">{formatNumber(row.OUT_PROVIDE_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.OUT_VAT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.OUT_TOTAL_AMOUNT)}</span></td>
                                                <td style={!isSubtotal && row.UNPAID_AMOUNT_OUT > 0 ? { background: '#faf60fff' } : {}}>
                                                    <span className="right-align">{formatNumber(row.UNPAID_AMOUNT_OUT)}</span>
                                                </td>

                                                <td><span className="right-align">{formatNumber(row.IN_PROVIDE_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.IN_VAT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.IN_TOTAL_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.IN_PAID_AMOUNT)}</span></td>
                                                <td style={!isSubtotal && row.REMAIN_AMOUNT > 0 ? { background: '#faf60fff' } : {}}>
                                                    <span className="right-align">{formatNumber(row.REMAIN_AMOUNT)}</span>
                                                </td>

                                                {/* 차액 */}
                                                <td><span className="right-align" style={{ fontWeight: 'bold' }}>{isSubtotal ? formatNumber(diff) : ''}</span></td>

                                                <td>{row.PAID_DATE}</td>
                                                <td>
                                                  <span style={{
                                                    textAlign: 'left',
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    display: 'block',
                                                    maxWidth: '200px'
                                                  }}>
                                                    {row.REMARK}
                                                  </span>
                                                </td>
                                            </tr>
                                        );
                                    }
                                    i += spanCount;
                                }
                                return result;
                            })()}

                            {/* 메인 테이블 합계 행 */}
                            <tr className="total-row">
                                <td colSpan={3}><strong>합계</strong></td>
                                <td><span className="right-align">{formatNumber(totalRow.OUT_PROVIDE_AMOUNT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.OUT_VAT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.OUT_TOTAL_AMOUNT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.UNPAID_AMOUNT_OUT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.IN_PROVIDE_AMOUNT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.IN_VAT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.IN_TOTAL_AMOUNT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.IN_PAID_AMOUNT)}</span></td>
                                <td><span className="right-align">{formatNumber(totalRow.REMAIN_AMOUNT)}</span></td>
                                <td><span className="right-align" style={{ color: 'blue', fontWeight: 'bold' }}>{formatNumber((totalRow.OUT_TOTAL_AMOUNT || 0) - (totalRow.IN_TOTAL_AMOUNT || 0))}</span></td>
                                <td></td><td></td>
                            </tr>


<tr>
                                    <th style={{ width: '100px' }} rowSpan="5"><strong>총괄</strong></th>
                                    <th style={{ width: '200px' }} rowSpan="2" colSpan="2"><strong>구분</strong></th>
                                    <th colSpan="4"><strong>매출</strong></th>
                                    <th colSpan="5"><strong>매입</strong></th>
                                    <th colSpan="3"><strong>매출-매입(차액)</strong></th>
                                </tr>
                                <tr>
                                    <th>공급가액</th><th>세액</th><th>합계</th><th>미결금</th>
                                    <th>공급가액</th><th>세액</th><th>합계</th><th>결제금액</th><th>잔금</th>
                                    <th>공급가</th><th>부가세</th><th>합계</th>
                                </tr>
                                
                                {/* 1. 세금계산서 합계 (ETC_YN != 'Y') */}
                                <tr style={{marginTop: '50px'}}>
                                    <td colSpan="2"><strong>세금계산서 합계</strong></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.OUT_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.OUT_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.OUT_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.UNPAID_AMOUNT_OUT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.IN_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.IN_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.IN_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.IN_PAID_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(taxInvoiceRow.REMAIN_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber((taxInvoiceRow.OUT_PROVIDE_AMOUNT || 0) - (taxInvoiceRow.IN_PROVIDE_AMOUNT || 0))}</span></td>
                                    <td><span className="right-align">{formatNumber((taxInvoiceRow.OUT_VAT || 0) - (taxInvoiceRow.IN_VAT || 0))}</span></td>
                                    <td><span className="right-align">{formatNumber((taxInvoiceRow.OUT_TOTAL_AMOUNT || 0) - (taxInvoiceRow.IN_TOTAL_AMOUNT || 0))}</span></td>
                                </tr>

                                {/* 2. 기타경비 합계 (ETC_YN == 'Y') */}
                                <tr>
                                    <td colSpan="2"><strong>기타경비</strong></td>
                                    <td><span className="right-align">{formatNumber(etcRow.OUT_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.OUT_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.OUT_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.UNPAID_AMOUNT_OUT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.IN_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.IN_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.IN_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.IN_PAID_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(etcRow.REMAIN_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber((etcRow.OUT_PROVIDE_AMOUNT || 0) - (etcRow.IN_PROVIDE_AMOUNT || 0))}</span></td>
                                    <td><span className="right-align">{formatNumber((etcRow.OUT_VAT || 0) - (etcRow.IN_VAT || 0))}</span></td>
                                    <td><span className="right-align">{formatNumber((etcRow.OUT_TOTAL_AMOUNT || 0) - (etcRow.IN_TOTAL_AMOUNT || 0))}</span></td>
                                </tr>

                                {/* 3. 전체 합계 */}
                                <tr>
                                    <td colSpan="2"><strong>합계</strong></td>
                                    <td><span className="right-align">{formatNumber(totalRow.OUT_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.OUT_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.OUT_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.UNPAID_AMOUNT_OUT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.IN_PROVIDE_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.IN_VAT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.IN_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.IN_PAID_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.REMAIN_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber((totalRow.OUT_PROVIDE_AMOUNT || 0) - (totalRow.IN_PROVIDE_AMOUNT || 0))}</span></td>
                                    <td><span className="right-align">{formatNumber((totalRow.OUT_VAT || 0) - (totalRow.IN_VAT || 0))}</span></td>
                                    <td style={{ background: '#faf60fff'}}><span className="right-align">{formatNumber((totalRow.OUT_TOTAL_AMOUNT || 0) - (totalRow.IN_TOTAL_AMOUNT || 0))}</span></td>
                                </tr>

                        </tbody>
                    </table>
 
                </div>
            </div>
        </div>
    );
};

export default TestPage4;